import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import type { z } from "zod";

const PREFILL_QUERY_PARAM = "prefill";

/**
 * Reads the handover a page was opened with: a JSON `prefill` query param,
 * parsed once on mount against the caller's schema and then cleared from the
 * URL, so a refresh doesn't replay it. A param that doesn't parse reads as no
 * prefill. The value survives the clearing, so the caller can act on it after
 * the URL has changed.
 */
export function usePrefillFromUrl<Schema extends z.ZodType>(
  schema: Schema,
): z.infer<Schema> | null {
  const [searchParams, setSearchParams] = useSearchParams();

  const [prefill] = useState<z.infer<Schema> | null>(() => {
    const prefillParam = searchParams.get(PREFILL_QUERY_PARAM);
    if (!prefillParam) {
      return null;
    }
    try {
      return schema.parse(JSON.parse(prefillParam));
    } catch {
      return null;
    }
  });

  const hasClearedPrefillRef = useRef(false);
  useEffect(() => {
    if (
      hasClearedPrefillRef.current ||
      !searchParams.has(PREFILL_QUERY_PARAM)
    ) {
      return;
    }
    hasClearedPrefillRef.current = true;
    setSearchParams(
      (prev) => {
        prev.delete(PREFILL_QUERY_PARAM);
        return prev;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  return prefill;
}
